
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { getMedicalRecordById, saveMedicalRecord } from '@/services/medicalService';
import { MedicalRecord } from '@/hooks/useMedicalRecords';
import RecordFormFields from './RecordFormFields';
import { RecordFormData } from './types';
import { FileList } from './FileList';
import { MedicalRecordLimitBanner } from '@/components/medical/MedicalRecordLimitBanner';
import { useMedicalRecordLimits } from '@/hooks/useMedicalRecordLimits';
import { useTranslation } from 'react-i18next';

export default function AddEditMedicalRecordForm() {
  const { t } = useTranslation();
  const { petId, recordId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = recordId && recordId !== 'new';
  const { hasReachedLimit, allowPrescriptions } = useMedicalRecordLimits(petId);

  const [formData, setFormData] = useState<RecordFormData>({
    title: '',
    record_type: 'general_note',
    record_date: new Date(),
    description: '',
    vet_name: '',
  });
  
  const [files, setFiles] = useState<FileList | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && recordId) {
      setLoading(true);
      getMedicalRecordById(recordId)
        .then((data: MedicalRecord) => {
          setFormData({
            title: data.title || '',
            record_type: data.record_type || 'general_note',
            record_date: new Date(data.record_date),
            description: data.description || '',
            vet_name: data.vet_name || '',
          });
          
          if (data.attachments) {
            setExistingAttachments(data.attachments);
          }
        })
        .catch(err => {
          setError(t('medical.loadRecordError'));
          toast({
            title: t('common.error'),
            description: t('medical.loadRecordError'),
            variant: "destructive"
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [recordId, isEditing, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.title.trim()) {
      setError(t('medical.titleRequired'));
      setLoading(false);
      return;
    }

    // Check if user has reached limit (only for new records)
    if (!isEditing && hasReachedLimit) {
      setError(t('medical.limitReached'));
      toast({
        title: t('medical.limitReachedTitle'),
        description: t('medical.limitReached'),
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    // Check prescription upload permission
    if (!isEditing && files && files.length > 0 && !allowPrescriptions) {
      setError(t('medical.upgradeForPrescriptions'));
      toast({
        title: t('medical.featureNotAvailable'),
        description: t('medical.upgradeForPrescriptions'),
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    try {
      await saveMedicalRecord(
        petId as string,
        {
          ...formData,
          attachments: files ? Array.from(files) : [],
          existingAttachments,
        },
        isEditing ? recordId : undefined
      );

      toast({
        title: isEditing ? t('medical.recordUpdated') : t('medical.recordAdded'),
        description: t('medical.recordSaved'),
      });

      navigate(`/medical/${petId}/records`);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to save medical record";
      setError(errorMessage);
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return <div className="p-4 text-center">{t('medical.loadingDetails')}</div>;
  }

  return (
    <div className="flex-1 overflow-auto">
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {!isEditing && petId && (
          <MedicalRecordLimitBanner petId={petId} type={hasReachedLimit ? 'limit' : 'warning'} />
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <RecordFormFields 
          formData={formData}
          setFormData={setFormData}
          files={files}
          setFiles={setFiles}
          allowPrescriptions={allowPrescriptions}
        />

        {existingAttachments.length > 0 && (
          <FileList attachments={existingAttachments} />
        )}

        <Button
          type="submit"
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={loading || (!isEditing && hasReachedLimit)}
        >
          {loading ? t('common.saving') : isEditing ? t('medical.updateRecord') : t('medical.addRecordButton')}
        </Button>
      </form>
    </div>
  );
}
